import React from "react";
import Icon from "../../../components/AppIcon";

const CulturalEncounters = ({ culturalHighlights }) => {
  if (!culturalHighlights?.length) return null;

  return (
    <div className="bg-white py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="section-heading text-foreground mb-3">
            <span className="text-accent">Cultural</span> Encounters
          </h2>
          <div className="w-16 h-1 bg-accent rounded-full mx-auto mb-4" />
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Immerse yourself in the rich Sherpa culture and Buddhist traditions
            that have thrived in these mountains for centuries.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {culturalHighlights?.map((highlight, index) => (
            <div
              key={index}
              className="bg-[#fafaf7] rounded-2xl overflow-hidden border border-border/50 hover:shadow-md transition-all duration-300"
            >
              <div className="relative overflow-hidden" style={{ paddingTop: "56.25%" }}>
                <img
                  src={highlight.image}
                  alt={highlight?.title}
                  className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  onError={(e) => { e.target.src = "/assets/images/no_image.png"; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/90 backdrop-blur-sm text-xs font-semibold text-primary rounded-full">
                    <Icon name={highlight?.icon} size={12} />
                    {highlight?.category}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <h3 className="font-headline text-xl font-semibold text-foreground mb-3">
                  {highlight?.title}
                </h3>

                <p className="text-text-secondary mb-4 leading-relaxed text-sm">
                  {highlight?.description}
                </p>

                <div className="space-y-2">
                  {highlight?.experiences?.map((experience, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <div className="w-5 h-5 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon name="Check" size={12} className="text-success" />
                      </div>
                      <span className="text-sm text-text-secondary">{experience}</span>
                    </div>
                  ))}
                </div>

                {highlight?.bestTime && (
                  <div className="mt-4 pt-4 border-t border-border/50 flex items-center gap-2 text-sm text-text-secondary">
                    <Icon name="Calendar" size={14} className="text-accent" />
                    <span>Best experienced: {highlight?.bestTime}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#fafaf7] rounded-2xl border border-border/50 p-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="Heart" size={22} className="text-primary" />
            </div>
            <div>
              <h3 className="font-headline text-2xl font-semibold text-foreground mb-2">
                Cultural Etiquette & Respect
              </h3>
              <p className="text-text-secondary text-sm">
                Understanding and respecting local customs enhances your
                experience and shows appreciation for Sherpa hospitality.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="ThumbsUp" size={18} className="text-success" />
                <span>Do's</span>
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="Check" size={12} className="text-success" />
                  </div>
                  <span className="text-text-secondary text-sm">Remove shoes before entering homes and monasteries</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="Check" size={12} className="text-success" />
                  </div>
                  <span className="text-text-secondary text-sm">Walk clockwise around monasteries and stupas</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="Check" size={12} className="text-success" />
                  </div>
                  <span className="text-text-secondary text-sm">Accept tea and food offerings graciously</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="Check" size={12} className="text-success" />
                  </div>
                  <span className="text-text-secondary text-sm">Ask permission before photographing people</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="Check" size={12} className="text-success" />
                  </div>
                  <span className="text-text-secondary text-sm">Dress modestly, especially at religious sites</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="ThumbsDown" size={18} className="text-error" />
                <span>Don'ts</span>
              </h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="X" size={12} className="text-error" />
                  </div>
                  <span className="text-text-secondary text-sm">Don't touch religious artifacts or statues</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="X" size={12} className="text-error" />
                  </div>
                  <span className="text-text-secondary text-sm">Don't point feet toward altars or people</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="X" size={12} className="text-error" />
                  </div>
                  <span className="text-text-secondary text-sm">Don't refuse hospitality outright - explain politely</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="X" size={12} className="text-error" />
                  </div>
                  <span className="text-text-secondary text-sm">Don't wear revealing clothing in villages</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name="X" size={12} className="text-error" />
                  </div>
                  <span className="text-text-secondary text-sm">Don't bargain aggressively - be respectful</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CulturalEncounters;
